import { useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Filter, X, ExternalLink, Copy, Tag, Clock, Briefcase, ArrowUpDown, ArrowUp, ArrowDown, LineChart, EyeOff } from 'lucide-react'
import { LivePositionsWidget } from '@/components/common/LivePositionsWidget'
import { Header } from '@/components/layout/Header'
import { useAlertsOverviewFull } from '@/hooks/useAlerts'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import { ListingTagButton } from '@/components/common/ListingTagButton'
import { IgnoreListButton } from '@/components/common/IgnoreListButton'
import { Badge } from '@/components/ui/badge'
import { formatPercent, extractBaseSymbol, buildCoinglassUrl, getRelativeTime } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { addTag, AlertType } from '@/lib/api'
import { FEATURE_SCREENER_ENABLED } from '@/lib/featureFlags'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const ALERT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'oi', label: 'OI' },
  { value: 'orderbook', label: 'Orderbook' },
  { value: 'mark_price', label: 'Mark Price' },
  { value: 'funding', label: 'Funding' },
  { value: 'price_diff', label: 'Price Diff' },
]

const TIME_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: '1h', label: 'Last 1h' },
  { value: '6h', label: 'Last 6h' },
  { value: '12h', label: 'Last 12h' },
  { value: '24h', label: 'Last 24h' },
  { value: '48h', label: 'Last 48h' },
]

const MARKET_TYPES = [
  { value: 'all', label: 'All Markets' },
  { value: 'futures', label: 'Futures' },
  { value: 'spot', label: 'Spot' },
]

const ALERT_TYPE_COLORS: Record<string, string> = {
  oi: 'bg-blue-500/20 text-blue-400',
  orderbook: 'bg-purple-500/20 text-purple-400',
  mark_price: 'bg-orange-500/20 text-orange-400',
  price_diff: 'bg-green-500/20 text-green-400',
}

export default function Dashboard() {
  const exchangeOptions = useExchangeOptions()
  // Filter state
  const [exchangeFilter, setExchangeFilter] = useState<string[]>([])
  const [alertType, setAlertType] = useState<string>('all')
  const [timeRange, setTimeRange] = useState<string>('24h')
  const [marketType, setMarketType] = useState<string>('all')
  const [tagsFilter, setTagsFilter] = useState<string>('')
  const [showFilters, setShowFilters] = useState(true)
  const [selectedAlert, setSelectedAlert] = useState<Record<string, unknown> | null>(null)

  // Dismissed alerts state
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('dismissed-alerts')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch {
      return new Set()
    }
  })

  const { toast } = useToast()

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>('time')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Build API params from filter state
  const apiParams = useMemo(() => {
    const params: any = {}
    if (exchangeFilter.length) {
      params.exchange = exchangeFilter.map((exchange: string) => exchange.toLowerCase()).join(',')
    }
    if (alertType !== 'all') params.alert_type = alertType
    if (timeRange !== 'all') params.time_range = timeRange
    if (marketType !== 'all') params.market_type = marketType
    if (tagsFilter.trim()) params.tags = tagsFilter.trim()
    return params
  }, [exchangeFilter, alertType, timeRange, marketType, tagsFilter])

  const { data: alertsData, isLoading: alertsLoading } = useAlertsOverviewFull(apiParams)

  const rawAlerts = useMemo(() => {
    if (!alertsData?.alerts) return []
    return alertsData.alerts.filter(alert => {
      const ts = Number(alert.timestamp || alert.first_seen_ts || 0)
      const id = `${alert.alert_type}-${alert.exchange}-${alert.symbol}-${ts}`
      return !dismissedAlerts.has(id)
    })
  }, [alertsData, dismissedAlerts])
  const summary = alertsData?.summary || { total_count: 0, by_type: {}, by_exchange: {}, by_symbol: {} }

  // Sort alerts based on current sort column and direction
  const alerts = useMemo(() => {
    if (!sortColumn) return rawAlerts

    return [...rawAlerts].sort((a, b) => {
      let aVal: string | number = ''
      let bVal: string | number = ''

      switch (sortColumn) {
        case 'time':
          aVal = Number(a.timestamp || a.first_seen_ts || 0)
          bVal = Number(b.timestamp || b.first_seen_ts || 0)
          break
        case 'type':
          aVal = String(a.alert_type || '')
          bVal = String(b.alert_type || '')
          break
        case 'exchange':
          aVal = String(a.exchange || '').toLowerCase()
          bVal = String(b.exchange || '').toLowerCase()
          break
        case 'symbol':
          aVal = String(a.symbol || a.base || '').toLowerCase()
          bVal = String(b.symbol || b.base || '').toLowerCase()
          break
        case 'value':
          // Extract numeric value based on alert type
          const aType = String(a.alert_type || '')
          const bType = String(b.alert_type || '')
          if (aType === 'oi') aVal = Math.abs(Number(a.oi_change_pct || 0))
          else if (aType === 'orderbook') aVal = Number(a.usd_value || 0)
          else if (aType === 'mark_price') aVal = Math.abs(Number(a.deviation_pct || a.threshold_pct || 0))
          else if (aType === 'price_diff') aVal = Math.abs(Number(a.max_diff_pct || 0))

          if (bType === 'oi') bVal = Math.abs(Number(b.oi_change_pct || 0))
          else if (bType === 'orderbook') bVal = Number(b.usd_value || 0)
          else if (bType === 'mark_price') bVal = Math.abs(Number(b.deviation_pct || b.threshold_pct || 0))
          else if (bType === 'price_diff') bVal = Math.abs(Number(b.max_diff_pct || 0))
          break
        default:
          return 0
      }

      // Compare values
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }

      // String comparison
      const aStr = String(aVal)
      const bStr = String(bVal)
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    })
  }, [rawAlerts, sortColumn, sortDirection])

  const hasActiveFilters = exchangeFilter.length > 0 || alertType !== 'all' ||
    timeRange !== '24h' || marketType !== 'all' || tagsFilter.trim()

  const clearFilters = () => {
    setExchangeFilter([])
    setAlertType('all')
    setTimeRange('24h')
    setMarketType('all')
    setTagsFilter('')
  }

  const toggleExchangeFilter = useCallback((exchange: string) => {
    setExchangeFilter((current) => (
      current.includes(exchange)
        ? current.filter((value) => value !== exchange)
        : [...current, exchange]
    ))
  }, [])

  const clearExchangeFilter = useCallback(() => {
    setExchangeFilter([])
  }, [])

  const handleCloseAlertDetail = useCallback(() => {
    setSelectedAlert(null)
  }, [])

  const getAlertValue = (alertTypeValue: string, alert: Record<string, unknown>) => {
    if (alertTypeValue === 'oi') {
      const pct = Number(alert.oi_change_pct || 0)
      const period = alert.oi_period ? ` (${alert.oi_period})` : ''
      return {
        value: formatPercent(pct) + period,
        className: pct >= 0 ? 'text-green-500' : 'text-red-500',
      }
    }
    if (alertTypeValue === 'orderbook') {
      const usd = Number(alert.usd_value || 0)
      const price = alert.price ? Number(alert.price) : null
      const side = String(alert.side || '').toUpperCase()
      const priceStr = price ? ` @ $${price.toFixed(price > 1 ? 2 : 6)}` : ''
      const sideStr = side ? ` (${side})` : ''
      return {
        value: `$${(usd / 1000).toFixed(0)}K${priceStr}${sideStr}`,
        className: String(alert.side) === 'bid' ? 'text-green-500' : 'text-red-500',
      }
    }
    if (alertTypeValue === 'mark_price') {
      const pct = Number(alert.deviation_pct || alert.threshold_pct || 0)
      return {
        value: `${pct.toFixed(2)}%`,
        className: 'text-orange-500',
      }
    }
    if (alertTypeValue === 'price_diff') {
      const pct = Number(alert.max_diff_pct || 0)
      const outlierExchanges = alert.outlier_exchanges as string[] | undefined
      let exchangeStr = ''
      if (outlierExchanges && outlierExchanges.length > 0) {
        const capitalized = outlierExchanges.map(ex => ex.charAt(0).toUpperCase() + ex.slice(1))
        exchangeStr = ` (${capitalized.join(' ↔ ')})`
      }
      return {
        value: `${pct.toFixed(2)}%${exchangeStr}`,
        className: 'text-green-500',
      }
    }
    return { value: '-', className: '' }
  }

  const handleDismissAlert = useCallback((alert: Record<string, unknown>, e: React.MouseEvent) => {
    e.stopPropagation()
    const ts = Number(alert.timestamp || alert.first_seen_ts || 0)
    const id = `${alert.alert_type}-${alert.exchange}-${alert.symbol}-${ts}`

    setDismissedAlerts(prev => {
      const next = new Set(prev)
      next.add(id)
      localStorage.setItem('dismissed-alerts', JSON.stringify(Array.from(next)))
      return next
    })

    toast({
      title: "Alert dismissed",
      description: "Alert removed from dashboard",
    })
  }, [toast])

  const handleOpenScreener = useCallback((symbol: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const base = extractBaseSymbol(symbol)
    // Open in same tab or new tab? Let's use same tab but we need to pass the ticker
    // Since it's a SPA, we can just navigate, but window.open is used elsewhere. 
    // Let's use window.location.href to navigate
    window.location.href = `/screener?ticker=${base}`
  }, [])

  /* Removed legacy local toast state */

  const handleCopySymbol = useCallback((symbol: string) => {
    navigator.clipboard.writeText(symbol)
      .then(() => toast({ title: `Copied ${symbol}`, className: "text-green-500" }))
      .catch(() => toast({ title: 'Failed to copy', variant: "destructive" }))
  }, [toast])

  const handleCopyBaseSymbol = useCallback((symbol: string) => {
    const base = extractBaseSymbol(symbol)
    if (!base) {
      toast({ title: 'Failed to copy', variant: "destructive" })
      return
    }
    navigator.clipboard.writeText(base)
      .then(() => toast({ title: `Copied ${base}`, className: "text-green-500" }))
      .catch(() => toast({ title: 'Failed to copy', variant: "destructive" }))
  }, [toast])

  const handleAddSpamTag = useCallback(async (symbol: string) => {
    const base = extractBaseSymbol(symbol)
    if (base) {
      try {
        await addTag(base, 'spam')
        toast({ title: `Added SPAM tag to ${base}`, className: "text-green-500" })
      } catch {
        toast({ title: 'Failed to add tag', variant: "destructive" })
      }
    }
  }, [toast])

  const handleOpenPositions = useCallback((exchange: string, symbol: string) => {
    // Open positions page or external link
    const base = extractBaseSymbol(symbol)
    // Use window.open for external/new tab navigation
    window.location.href = `/positions?symbol=${base}&exchange=${exchange}`
  }, [])

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to descending for most columns, ascending for text
      setSortColumn(column)
      setSortDirection(column === 'symbol' || column === 'type' ? 'asc' : 'desc')
    }
  }, [sortColumn, sortDirection])

  const selectedAlertType = selectedAlert ? String(selectedAlert.alert_type || 'unknown') : ''
  const selectedSymbol = selectedAlert ? String(selectedAlert.symbol || selectedAlert.base || '') : ''
  const selectedExchange = selectedAlert ? String(selectedAlert.exchange || '') : ''
  const selectedAlertName = selectedAlert ? String(selectedAlert.alert_name || '') : ''
  const selectedTags = (selectedAlert?.tags as string[]) || []
  const selectedTimestamp = Number(selectedAlert?.timestamp || selectedAlert?.first_seen_ts || 0)
  const selectedOiPeriod: string = selectedAlert?.oi_period ? String(selectedAlert.oi_period) : ''
  const selectedOrderPrice = selectedAlert?.price !== undefined ? Number(selectedAlert.price) : null
  const selectedOrderSide = selectedAlert?.side !== undefined ? String(selectedAlert.side) : ''
  const selectedExchangePrices = (selectedAlert?.exchange_prices || null) as Record<string, number> | null
  const selectedOutlierExchanges = (selectedAlert?.outlier_exchanges || []) as string[]
  const selectedChartUrl = selectedAlert
    ? buildCoinglassUrl(selectedExchange, extractBaseSymbol(selectedSymbol))
    : ''
  const selectedValue = selectedAlert
    ? getAlertValue(selectedAlertType, selectedAlert)
    : { value: '-', className: '' }
  const selectedTimestampLabel = selectedTimestamp
    ? new Date(selectedTimestamp * 1000).toLocaleString()
    : '-'

  return (
    <div className="flex flex-col">
      <Header title="Dashboard" />

      <div className="flex-1 space-y-6 p-6">
        {/* Live Open Positions */}
        <LivePositionsWidget />

        {/* Exchange Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Alerts by Exchange</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {exchangeOptions.map((exchange) => {
                const count = summary.by_exchange?.[exchange.toLowerCase()] || 0
                return (
                  <div
                    key={exchange}
                    className={cn(
                      "flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors",
                      exchangeFilter.includes(exchange) ? "bg-primary/10 border-primary" : "hover:bg-muted"
                    )}
                    onClick={() => toggleExchangeFilter(exchange)}
                  >
                    <ExchangeBadge exchange={exchange} />
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Alerts Overview Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-base">Alerts Overview</CardTitle>
                <Badge variant="secondary" className="text-xs">
                  {summary.total_count} total
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <X className="mr-1 h-3 w-3" />
                    Clear
                  </Button>
                )}
                <Button
                  variant={showFilters ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="h-8"
                >
                  <Filter className="mr-1 h-3 w-3" />
                  Filters
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 flex items-center justify-center text-xs">
                      !
                    </Badge>
                  )}
                </Button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 space-y-4 p-4 rounded-lg border bg-muted/50">
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                  {/* Exchange Filter */}
                  <div className="space-y-2">
                    <Label>Exchange</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={clearExchangeFilter}
                        className={cn(
                          "flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                          exchangeFilter.length === 0 ? "bg-primary/10 border-primary" : "hover:bg-muted"
                        )}
                      >
                        All
                      </button>
                      {exchangeOptions.map((exchange) => (
                        <button
                          key={exchange}
                          type="button"
                          onClick={() => toggleExchangeFilter(exchange)}
                          className={cn(
                            "flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                            exchangeFilter.includes(exchange) ? "bg-primary/10 border-primary" : "hover:bg-muted"
                          )}
                        >
                          <ExchangeBadge exchange={exchange} />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Alert Type Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="type-filter">Alert Type</Label>
                    <Select value={alertType} onValueChange={setAlertType}>
                      <SelectTrigger id="type-filter">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        {ALERT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Time Range */}
                  <div className="space-y-2">
                    <Label htmlFor="time-filter">Time Range</Label>
                    <Select value={timeRange} onValueChange={setTimeRange}>
                      <SelectTrigger id="time-filter">
                        <SelectValue placeholder="All Time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_RANGES.map((range) => (
                          <SelectItem key={range.value} value={range.value}>
                            {range.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Market Type */}
                  <div className="space-y-2">
                    <Label htmlFor="market-filter">Market Type</Label>
                    <Select value={marketType} onValueChange={setMarketType}>
                      <SelectTrigger id="market-filter">
                        <SelectValue placeholder="All Markets" />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKET_TYPES.map((mt) => (
                          <SelectItem key={mt.value} value={mt.value}>
                            {mt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tags Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="tags-filter">Tags</Label>
                    <Input
                      id="tags-filter"
                      placeholder="watch, hot"
                      value={tagsFilter}
                      onChange={(e) => setTagsFilter(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {alertsLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading alerts...</div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No alerts match the current filters
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">
                        <button
                          type="button"
                          onClick={() => handleSort('time')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Time
                          {sortColumn === 'time' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          )}
                          {sortColumn !== 'time' && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                      <TableHead className="w-[100px]">
                        <button
                          type="button"
                          onClick={() => handleSort('type')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Type
                          {sortColumn === 'type' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          )}
                          {sortColumn !== 'type' && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                      <TableHead className="w-[100px]">
                        <button
                          type="button"
                          onClick={() => handleSort('exchange')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Exchange
                          {sortColumn === 'exchange' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          )}
                          {sortColumn !== 'exchange' && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSort('symbol')}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Symbol
                          {sortColumn === 'symbol' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          )}
                          {sortColumn !== 'symbol' && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                      <TableHead>Alert</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead className="text-right">
                        <button
                          type="button"
                          onClick={() => handleSort('value')}
                          className="flex items-center gap-1 ml-auto hover:text-primary transition-colors"
                        >
                          Value
                          {sortColumn === 'value' && (
                            sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          )}
                          {sortColumn !== 'value' && <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.slice(0, 50).map((alert: Record<string, unknown>, idx: number) => {
                      const alertType = String(alert.alert_type || 'unknown')
                      const symbol = String(alert.symbol || alert.base || '')
                      const exchange = String(alert.exchange || '')
                      const alertName = String(alert.alert_name || '')
                      const tags = (alert.tags as string[]) || []
                      const timestamp = Number(alert.timestamp || alert.first_seen_ts || 0)
                      const chartUrl = buildCoinglassUrl(exchange, extractBaseSymbol(symbol))

                      const { value, className: valueClass } = getAlertValue(alertType, alert)

                      return (
                        <TableRow
                          key={`${alertType}-${exchange}-${symbol}-${idx}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedAlert(alert)}
                        >
                          <TableCell className="text-muted-foreground text-xs">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {timestamp ? getRelativeTime(timestamp) : '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={cn('text-xs', ALERT_TYPE_COLORS[alertType] || '')}>
                              {alertType === 'mark_price' ? 'MP' : alertType === 'price_diff' ? 'PD' : alertType.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <ExchangeBadge exchange={exchange} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleCopyBaseSymbol(symbol)
                                }}
                                className="font-medium hover:text-primary"
                              >
                                {symbol}
                              </button>
                              <a
                                href={chartUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                className="text-muted-foreground hover:text-primary"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {alertName || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                              {tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {tags.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={cn('text-right font-semibold', valueClass)}>
                            {value}
                          </TableCell>
                          <TableCell>
                            <TooltipProvider delayDuration={200}>
                              <div className="flex items-center gap-1">
                                <Button asChild variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={(event) => event.stopPropagation()}>
                                  <Link to={`/alert-reviews?symbol=${encodeURIComponent(extractBaseSymbol(symbol))}`}>Review</Link>
                                </Button>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        handleCopySymbol(symbol)
                                      }}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Copy symbol</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        handleAddSpamTag(symbol)
                                      }}
                                    >
                                      <Tag className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Add SPAM tag</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        handleOpenPositions(exchange, symbol)
                                      }}
                                    >
                                      <Briefcase className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Open position</TooltipContent>
                                </Tooltip>
                                {FEATURE_SCREENER_ENABLED && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={(e) => handleOpenScreener(symbol, e)}
                                    >
                                      <LineChart className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Open in Screener</TooltipContent>
                                </Tooltip>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 hover:text-red-500"
                                      onClick={(e) => handleDismissAlert(alert, e)}
                                    >
                                      <EyeOff className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Dismiss</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <ListingTagButton
                                      symbol={extractBaseSymbol(symbol)}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      showLabel={false}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>Add Listing tag (24h)</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <IgnoreListButton
                                      symbol={extractBaseSymbol(symbol)}
                                      alertType={alertType as AlertType}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      showLabel={false}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>Add to ignore list</TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            {alerts.length > 50 && (
              <div className="text-center text-sm text-muted-foreground mt-4">
                Showing 50 of {alerts.length} alerts
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!selectedAlert} onOpenChange={(open) => !open && handleCloseAlertDetail()}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Alert Details</DialogTitle>
              <DialogDescription>
                {selectedAlertType ? selectedAlertType.replace('_', ' ').toUpperCase() : 'Alert'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 text-sm">
              <div className="flex flex-wrap items-center gap-3">
                <Badge className={cn('text-xs', ALERT_TYPE_COLORS[selectedAlertType] || '')}>
                  {selectedAlertType === 'mark_price'
                    ? 'MP'
                    : selectedAlertType === 'price_diff'
                      ? 'PD'
                      : selectedAlertType.toUpperCase()}
                </Badge>
                <ExchangeBadge exchange={selectedExchange} />
                <span className="text-muted-foreground">{selectedTimestampLabel}</span>
              </div>
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Symbol</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => selectedSymbol && handleCopyBaseSymbol(selectedSymbol)}
                      className="font-medium hover:text-primary"
                    >
                      {selectedSymbol || '-'}
                    </button>
                    <a
                      href={selectedChartUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Alert</span>
                  <span>{selectedAlertName || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Value</span>
                  <span className={cn('font-semibold', selectedValue.className)}>
                    {selectedValue.value}
                  </span>
                </div>

                {/* Orderbook-specific fields */}
                {selectedAlertType === 'orderbook' && selectedOrderPrice !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Order Price</span>
                    <span className="font-mono">
                      ${selectedOrderPrice.toFixed(selectedOrderPrice > 1 ? 2 : 6)}
                    </span>
                  </div>
                )}
                {selectedAlertType === 'orderbook' && selectedOrderSide && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Side</span>
                    <Badge className={selectedOrderSide === 'bid' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {selectedOrderSide.toUpperCase()}
                    </Badge>
                  </div>
                )}

                {/* OI-specific fields */}
                {selectedAlertType === 'oi' && selectedOiPeriod && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Period</span>
                    <span className="font-mono">{selectedOiPeriod}</span>
                  </div>
                )}

                {/* Price Diff-specific fields */}
                {selectedAlertType === 'price_diff' && selectedExchangePrices && Object.keys(selectedExchangePrices).length > 0 && (
                  <div className="flex items-start justify-between">
                    <span className="text-muted-foreground">Exchanges</span>
                    <div className="flex flex-col gap-1 text-right">
                      {Object.entries(selectedExchangePrices)
                        .sort(([, a], [, b]) => b - a)
                        .map(([exchange, price]) => (
                          <div key={exchange} className="flex items-center gap-2 justify-end">
                            <span className="capitalize text-xs">{exchange}</span>
                            <span className="font-mono text-xs">${Number(price).toFixed(2)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                {selectedAlertType === 'price_diff' && selectedOutlierExchanges.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Outliers</span>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {selectedOutlierExchanges.map((ex) => (
                        <Badge key={ex} variant="outline" className="text-xs capitalize">
                          {ex}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tags</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {selectedTags.length ? (
                      selectedTags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedSymbol && handleCopySymbol(selectedSymbol)}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy symbol
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedSymbol && handleAddSpamTag(selectedSymbol)}
                >
                  <Tag className="mr-2 h-4 w-4" />
                  Add SPAM tag
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedSymbol && handleOpenPositions(selectedExchange, selectedSymbol)}
                >
                  <Briefcase className="mr-2 h-4 w-4" />
                  Open position
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
